import type { DataTableFilterField } from '@/components/data-table/types'
import { field, createSchema } from '@/lib/store/schema'
import type { CallDutyOption } from '@/hooks/useCallDutyOptions'
import type { CallRubricOption } from '@/hooks/useCallRubricOptions'
import { Phone } from 'lucide-react'
import type { CallRecord } from '../types'
import { createRubricFilterFields } from '../rubricFilters'

/**
 * OpenStatus filter fields for the Call Logs v2 table.
 * Used by DataTableFilterControls (sidebar).
 */
const filtersBeforeDuty: DataTableFilterField<CallRecord>[] = [
  {
    label: 'Call ID',
    value: 'call_id',
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Date',
    value: 'created_at',
    type: 'timerange',
    defaultOpen: true,
  },
  {
    label: 'Type',
    value: 'type',
    type: 'checkbox',
    defaultOpen: true,
    compact: true,
    triggerIcon: Phone,
    options: [
      { label: 'Inbound', value: 'inbound' },
      { label: 'Outbound', value: 'outbound' },
    ],
  },
  {
    label: 'Duration',
    value: 'duration',
    type: 'slider',
    min: 0,
    max: 3600,
    unit: 's',
    defaultOpen: true,
  },
]

const filtersAfterDuty: DataTableFilterField<CallRecord>[] = [
  {
    label: 'Client Name',
    value: 'patient_name',
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Phone',
    value: 'phone',
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Ended Reason',
    value: 'ended_reason',
    type: 'input',
    defaultOpen: true,
  },
  {
    label: 'Label',
    value: 'label',
    type: 'input',
    defaultOpen: true,
  },
]

export function createCallsFilterFields(
  duties: CallDutyOption[] = [],
  dutyOptionsState: {
    state?: 'loading' | 'error' | 'ready'
    onRetry?: () => void
  } = {},
  rubrics: CallRubricOption[] = []
): DataTableFilterField<CallRecord>[] {
  const dutyFilter: DataTableFilterField<CallRecord> = {
    label: 'Duty',
    value: 'duty_id',
    type: 'checkbox',
    defaultOpen: true,
    compact: true,
    optionsState: dutyOptionsState.state,
    onOptionsRetry: dutyOptionsState.onRetry,
    options: duties.map((duty) => ({
      label: duty.status === 'archived' ? `${duty.name} (Archived)` : duty.name,
      value: duty.id,
    })),
  }

  return [
    ...filtersBeforeDuty,
    dutyFilter,
    ...filtersAfterDuty,
    ...createRubricFilterFields(rubrics),
  ]
}

/**
 * BYOS schema for URL serialization.
 * Each field maps to a filter in the sidebar.
 */
export const callsSchema = createSchema({
  call_id: field.string().default(null),
  created_at: field.array(field.timestamp()).delimiter('-').default([]),
  type: field.array(field.string()).delimiter(',').default([]),
  duration: field.array(field.number()).delimiter('-').default([]),
  duty_id: field.array(field.string()).delimiter(',').default([]),
  // Read-only compatibility for shared URLs created before the duty-ID picker.
  duty_name: field.string().default(null),
  patient_name: field.string().default(null),
  phone: field.string().default(null),
  ended_reason: field.string().default(null),
  label: field.string().default(null),
})

export type CallsFilterState = typeof callsSchema._type
